/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v4-mini";
import { remap as remap$ } from "../../lib/primitives.js";
import { safeParse } from "../../lib/schemas.js";
import { Result as SafeParseResult } from "../../types/fp.js";
import { SDKValidationError } from "../errors/sdkvalidationerror.js";
import {
  IssuerFieldMismatch,
  IssuerFieldMismatch$inboundSchema,
} from "./issuerfieldmismatch.js";
import {
  RemoteSessionIssuer,
  RemoteSessionIssuer$inboundSchema,
} from "./remotesessionissuer.js";

/**
 * An organization- or project-level remote_session_issuer that names the same upstream authorization server as a global issuer, and so could be consolidated onto it.
 */
export type IssuerConvergenceCandidate = {
  /**
   * Number of non-deleted remote_session_clients that would move onto the target issuer.
   */
  clientCount: number;
  /**
   * The authorization-server metadata fields (issuer, token_endpoint, authorization_endpoint) that differ from the target, with both sides' values. Non-empty blocks the migration.
   */
  endpointMismatches: Array<IssuerFieldMismatch>;
  /**
   * A remote_session_issuer record — upstream Authorization Server identity that Speakeasy speaks OAuth to.
   */
  issuer: RemoteSessionIssuer;
  /**
   * The organization that owns the candidate. Empty for a legacy project-scoped issuer written before this column existed.
   */
  organizationId: string;
  /**
   * Display name of the owning organization. Empty when the organization has no synced metadata.
   */
  organizationName: string;
  /**
   * Non-blocking divergences (oidc, passthrough, scopes_supported), with both sides' values. The target issuer's values become authoritative for the migrated clients.
   */
  warnings: Array<IssuerFieldMismatch>;
};

/** @internal */
export const IssuerConvergenceCandidate$inboundSchema: z.ZodMiniType<
  IssuerConvergenceCandidate,
  unknown
> = z.pipe(
  z.object({
    client_count: z.int(),
    endpoint_mismatches: z.array(IssuerFieldMismatch$inboundSchema),
    issuer: RemoteSessionIssuer$inboundSchema,
    organization_id: z.string(),
    organization_name: z.string(),
    warnings: z.array(IssuerFieldMismatch$inboundSchema),
  }),
  z.transform((v) => {
    return remap$(v, {
      "client_count": "clientCount",
      "endpoint_mismatches": "endpointMismatches",
      "organization_id": "organizationId",
      "organization_name": "organizationName",
    });
  }),
);

export function issuerConvergenceCandidateFromJSON(
  jsonString: string,
): SafeParseResult<IssuerConvergenceCandidate, SDKValidationError> {
  return safeParse(
    jsonString,
    (x) => IssuerConvergenceCandidate$inboundSchema.parse(JSON.parse(x)),
    `Failed to parse 'IssuerConvergenceCandidate' from JSON`,
  );
}
